/**
 * AttachmentField Component
 * 
 * Generic attachment field component for "Attach" and "Attach Image" field types.
 * Integrates with Dynamic Views and reuses AttachmentPickerDialog from Phase 3.
 * 
 * Architecture:
 * - Only knows about doctype, name, fieldname, and files
 * - No doctype-specific logic
 * - Uses generic attachment hooks from Phase 2
 * - Reuses AttachmentPickerDialog from Phase 3
 */

"use client";

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { 
  Upload, 
  File, 
  Image as ImageIcon, 
  X, 
  ExternalLink,
  Loader2,
  AlertCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AttachmentPickerDialog } from "@/components/attachments";
import { 
  useAttachments, 
  useDeleteAttachment 
} from "@/lib/attachments/hooks";
import { normalizeFileUrl, formatFileSize } from "@/lib/attachments/utils";
import type { DocFieldSchema } from "@/lib/erp/meta";
import type { AttachmentContext } from "@/lib/attachments/types";

interface AttachmentFieldProps {
  field: DocFieldSchema;
  value: string | null; // Current file_url (relative or absolute)
  onChange: (value: string | null) => void;
  disabled?: boolean;
  isImage?: boolean;
  doctype: string;
  name: string;
}

export function AttachmentField({
  field,
  value,
  onChange,
  disabled = false,
  isImage = false,
  doctype,
  name,
}: AttachmentFieldProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if document is ready (not new/unsaved)
  const isReady = !!doctype && !!name && name !== "new" && !name.startsWith("New ");

  // Build attachment context
  const context: AttachmentContext = useMemo(() => ({
    doctype,
    name,
    fieldname: field.fieldname,
  }), [doctype, name, field.fieldname]);

  // Fetch attachments for this field
  const { data: attachmentsData, isLoading: _isLoadingAttachments } = useAttachments(
    context,
    { enabled: isReady }
  );

  // Delete attachment mutation
  const deleteAttachment = useDeleteAttachment(context);

  // Find the current attachment
  const currentAttachment = useMemo(() => {
    if (!attachmentsData?.attachments || !value) return null;
    
    // Normalize value for comparison (handle both absolute and relative)
    const normalizedValue = normalizeFileUrl(value);
    
    return attachmentsData.attachments.find(
      att => {
        const normalizedAttUrl = normalizeFileUrl(att.file_url);
        return normalizedAttUrl === normalizedValue || att.file_url === value;
      }
    ) || null;
  }, [attachmentsData, value]);

  // Check if any mutation is pending
  const isPending = deleteAttachment.isPending;

  // Handle attach/replace completion
  const handleAttachCompleted = (result?: any) => {
    if (!result?.attachments) {
      setError("No attachment was created");
      return;
    }

    // Find the attachment for this field
    const attachmentForField = result.attachments.find(
      (att: any) => att.attached_to_field === field.fieldname
    ) ?? result.attachments[0];

    if (!attachmentForField?.file_url) {
      setError("Failed to get file URL from attachment");
      return;
    }

    // Normalize to relative path before calling onChange
    const normalizedUrl = normalizeFileUrl(attachmentForField.file_url);
    onChange(normalizedUrl);
    setError(null);
    // Do NOT close the dialog here - let user close it manually
    // Dialog will stay open so user can see the updated attachment or attach more files
  };

  // Handle remove
  const handleRemove = async () => {
    if (!value || !isReady) return;

    setError(null);

    try {
      // Prefer docname over file_url (safer)
      const attachment = currentAttachment || attachmentsData?.attachments.find(
        att => {
          const normalizedAttUrl = normalizeFileUrl(att.file_url);
          const normalizedValue = normalizeFileUrl(value);
          return normalizedAttUrl === normalizedValue || att.file_url === value;
        }
      );

      await deleteAttachment.mutateAsync(
        attachment?.docname
          ? { file_docname: attachment.docname }
          : { file_url: value }
      );

      // Clear the field value
      onChange(null);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to remove attachment";
      setError(errorMessage);
    }
  };

  // Handle open file
  const handleOpen = () => {
    if (!value) return;
    
    // If value is relative, make it absolute for opening
    const urlToOpen = value.startsWith("/") 
      ? `${window.location.origin}${value}`
      : value;
    
    window.open(urlToOpen, "_blank", "noopener,noreferrer");
  };

  const isRequired = field.reqd === 1;
  const hasAttachment = !!value;
  const showActions = hasAttachment && !disabled && isReady;

  return (
    <div className="space-y-3" data-field={field.fieldname}>
      <Label 
        htmlFor={field.fieldname} 
        className={cn(
          isRequired && "after:content-['*'] after:ml-0.5 after:text-destructive"
        )}
      >
        {field.label}
      </Label>

      {/* Helper text for new/unsaved documents */}
      {!isReady && (
        <p className="text-xs text-muted-foreground">
          Save the document before attaching files.
        </p>
      )}

      {/* Empty state */}
      {!hasAttachment && (
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsDialogOpen(true)}
            disabled={disabled || !isReady || isPending}
            className="rounded-full"
          >
            {isImage ? (
              <>
                <ImageIcon className="h-4 w-4 mr-2" />
                Attach Image
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Attach
              </>
            )}
          </Button>
        </div>
      )}

      {/* Has attachment state */}
      {hasAttachment && (
        <div className="flex items-start gap-3 p-3 rounded bg-muted/30 border border-border/30">
          {/* Image preview (for Attach Image) */}
          {isImage && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border border-border/40 hover:opacity-90 transition-opacity cursor-pointer"
                  disabled={disabled}
                >
                  <img
                    src={value}
                    alt={field.label || "Attachment"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to icon if image fails to load
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-2" side="right" align="start">
                <img
                  src={value}
                  alt={field.label || "Attachment"}
                  className="max-w-[200px] max-h-[200px] object-contain rounded-md"
                />
              </PopoverContent>
            </Popover>
          )}

          {/* File info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {!isImage && (
                <File className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {currentAttachment?.file_name || value.split("/").pop() || "File"}
                </p>
                {currentAttachment?.file_size && (
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(currentAttachment.file_size)}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            {showActions && (
              <div className="flex items-center gap-2 mt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDialogOpen(true)}
                  disabled={isPending}
                  className="h-8 text-xs rounded-full"
                >
                  Change
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  disabled={isPending}
                  className="h-8 text-xs rounded-full text-destructive hover:text-destructive"
                >
                  {deleteAttachment.isPending ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 mr-1" />
                      Remove
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleOpen}
                  disabled={isPending}
                  className="h-8 text-xs rounded-full"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Open
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Attachment Picker Dialog */}
      <AttachmentPickerDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        context={context}
        multiple={false}
        accept={isImage ? ['image/*'] : undefined}
        mode={hasAttachment ? 'replace' : 'attach'}
        onCompleted={handleAttachCompleted}
      />
    </div>
  );
}
